/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, memo, useContext, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import { Button, Col, Row, Space, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';
import CarouselCardButtons from './CarouselCardButtons';
import { EditIcon } from '../../../Icons/EditIcon';

// Misc imports
import { CAROUSEL_NODE_HANDLE_IDS, GRAPH_LAYOUTS } from '../../../../constants/flowGraph';
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { GraphContext } from '../../FlowGraph';

// Constant declarations
const { Text } = Typography;

const CarouselNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { cards } = data || {};
  const { graphDirection, openEditForm, elements } = useContext(GraphContext);
  const { primaryBotLanguage, currentBotLanguage } = useBotLanguage();

  const currentElement = useMemo(() =>
    elements.find((element) => element.id === id)
  , [elements, id]);

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'}/>

        <Row gutter={[12, 12]}>
          <Col span={24}>
            <Space size={6} className="node-header">
              {nodeProps.icon}
              {nodeProps.label}
            </Space>
          </Col>

          <Col span={24}>
            <Space
              direction={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'horizontal' : 'vertical'}
              align="start"
              className={classNames('justify-content-center mb-4', {
                'w-100': graphDirection === GRAPH_LAYOUTS.VERTICAL
              })}
            >
              {
                cards?.length > 0 ? cards.map((card, index) => {
                  return (
                    <span
                      key={index}
                      style={{ maxWidth: 250 }}
                      className={classNames('cardWrapper', {
                        'mr-10': cards.length !== index + 1,
                        'mr-0': cards.length === index + 1
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Col className="p-0">
                        <Row align="middle" justify="space-between" className="px-1 bg-layout border-radius-top-left-base border-radius-top-right-base">
                          <Text className="py-1 mr-2 text-left" ellipsis>{card[primaryBotLanguage]?.title}</Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditIcon />}
                            onClick={() => {
                              openEditForm(id, { propertyToFocus: String(index) });
                            }}
                          />
                        </Row>

                        <CarouselCardButtons
                          selectedNode={currentElement}
                          card={card}
                          cardIndex={index}
                        />
                      </Col>
                    </span>
                  );
                }) : (
                  <Text className="text-secondary">
                    {t('Click to add cards')}
                  </Text>
                )
              }
            </Space>
          </Col>
        </Row>

        <LimitedConnectionsHandle
          id={CAROUSEL_NODE_HANDLE_IDS.NEXT}
          source={id}
          type="source"
          position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}
          allowedOutgoingEdgesCount={1}
        />
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage, openEditForm, currentElement, cards, elements]); // Adding openEditForm to dependencies might cause performance issues, remove if it does

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
CarouselNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(CarouselNode);
